import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {useLocation, useHistory} from 'react-router-dom';
import {useSelector} from 'react-redux';

import {
  reduxSetSidePanelEditState,
  reduxSetUpdatedCompany,
} from 'appredux/actions';
import type {RootState} from 'appredux/reducers';
import {
  apiGetFdApplication,
  apiGetTraderForm,
  apiGetUserTraderApplications,
} from 'constants/Api';
import {ITraderApplication} from 'constants/interfaces';

import {
  APPLICATION_JOURNEY_STEPS,
  ApplicationJourneyStepKey,
  IApplicationJourneyStepDefinition,
} from './journeyConfig';
import {
  adaptApplicantSummary,
  adaptCompanySummary,
  adaptDocumentsSummary,
  adaptFarmSummary,
  adaptFinancialSummary,
  adaptJourneyMeta,
  adaptProductionSummary,
  adaptSupplierJourneySnapshot,
  getSupplierTraderApplication,
  resolveJourneyCompletionPercent,
  ISupplierJourneyApplicantSummary,
  ISupplierJourneyApplication,
  ISupplierJourneyCompanySummary,
  ISupplierJourneyDocumentsSummary,
  ISupplierJourneyFarmSummary,
  ISupplierJourneyFinancialSummary,
  ISupplierJourneyMeta,
  ISupplierJourneyProductionSummary,
  ISupplierJourneySnapshot,
} from './journeyAdapters';

interface IApplicationJourneyLocationState {
  origin?: string;
}

interface ISupplierApplicationJourneyProviderProps {
  applicationId: string;
  origin: string;
  children: React.ReactNode;
}

interface ISupplierApplicationJourneyContextValue {
  applicationId: string;
  origin: string;
  isLoading: boolean;
  errorMessage: string | null;
  application?: ISupplierJourneyApplication;
  traderApplication?: ITraderApplication;
  completion: number;
  activeStep: IApplicationJourneyStepDefinition;
  activeStepIndex: number;
  activeSubStepIndex: number;
  hasPrevious: boolean;
  hasNext: boolean;
  snapshot: ISupplierJourneySnapshot;
  applicantSummary: ISupplierJourneyApplicantSummary;
  companySummary: ISupplierJourneyCompanySummary;
  farmSummary: ISupplierJourneyFarmSummary;
  productionSummary: ISupplierJourneyProductionSummary;
  financialSummary: ISupplierJourneyFinancialSummary;
  documentsSummary: ISupplierJourneyDocumentsSummary;
  journeyMeta: ISupplierJourneyMeta;
  setApplication: React.Dispatch<
    React.SetStateAction<ISupplierJourneyApplication | undefined>
  >;
  setTraderApplication: React.Dispatch<
    React.SetStateAction<ITraderApplication | undefined>
  >;
  refreshJourney: () => Promise<void>;
  goToStep: (
    stepKey: ApplicationJourneyStepKey,
    options?: {subStepKey?: string},
  ) => void;
  goToSubStep: (subStepIndex: number) => void;
  goNext: () => void;
  goPrevious: () => void;
}

const SupplierApplicationJourneyContext =
  createContext<ISupplierApplicationJourneyContextValue | null>(null);

function lshSelectCompanyForm(
  traderApplication?: ITraderApplication,
): ITraderApplication['forms'][number] | null {
  const forms = traderApplication?.forms || [];
  return (
    forms.find((form) => String(form?.id || '') === 'private_company_form') ||
    forms[0] ||
    null
  );
}

export function SupplierApplicationJourneyProvider({
  applicationId,
  origin,
  children,
}: ISupplierApplicationJourneyProviderProps) {
  const history = useHistory();
  const location = useLocation<IApplicationJourneyLocationState>();
  const editState = useSelector(
    (state: RootState) => state.sidepanel?.editState,
  );
  const updatedCompany = useSelector(
    (state: RootState) => state.sidepanel?.updatedCompany,
  );

  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [application, setApplication] = useState<ISupplierJourneyApplication>();
  const [traderApplication, setTraderApplication] =
    useState<ITraderApplication>();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const requestedStepPath = searchParams.get('step');
  const requestedSubStepKey = searchParams.get('subStep');

  const activeStep = useMemo<IApplicationJourneyStepDefinition>(() => {
    return (
      APPLICATION_JOURNEY_STEPS.find(
        (step) => step.path === requestedStepPath,
      ) || APPLICATION_JOURNEY_STEPS[0]
    );
  }, [requestedStepPath]);

  const activeStepIndex = useMemo(
    () =>
      APPLICATION_JOURNEY_STEPS.findIndex(
        (step) => step.key === activeStep.key,
      ),
    [activeStep.key],
  );

  const activeSubStepIndex = useMemo(() => {
    if (!activeStep.subSteps || activeStep.subSteps.length === 0) return 0;
    const matchedIndex = activeStep.subSteps.findIndex(
      (subStep) => subStep.key === requestedSubStepKey,
    );
    return matchedIndex >= 0 ? matchedIndex : 0;
  }, [activeStep.subSteps, requestedSubStepKey]);

  const replaceJourneySearch = useCallback(
    (nextParams: URLSearchParams) => {
      const nextSearch = nextParams.toString();
      history.replace({
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        state: location.state,
      });
    },
    [history, location.pathname, location.state],
  );

  const goToStep = useCallback(
    (stepKey: ApplicationJourneyStepKey, options?: {subStepKey?: string}) => {
      const nextStep =
        APPLICATION_JOURNEY_STEPS.find((step) => step.key === stepKey) ||
        APPLICATION_JOURNEY_STEPS[0];
      const nextParams = new URLSearchParams(location.search);
      nextParams.set('step', nextStep.path);

      if (nextStep.subSteps && nextStep.subSteps.length > 0) {
        nextParams.set(
          'subStep',
          options?.subStepKey || nextStep.subSteps[0].key,
        );
      } else {
        nextParams.delete('subStep');
      }

      replaceJourneySearch(nextParams);
    },
    [location.search, replaceJourneySearch],
  );

  const goToSubStep = useCallback(
    (subStepIndex: number) => {
      if (!activeStep.subSteps || !activeStep.subSteps[subStepIndex]) return;

      const nextParams = new URLSearchParams(location.search);
      nextParams.set('step', activeStep.path);
      nextParams.set('subStep', activeStep.subSteps[subStepIndex].key);
      replaceJourneySearch(nextParams);
    },
    [
      activeStep.path,
      activeStep.subSteps,
      location.search,
      replaceJourneySearch,
    ],
  );

  const goNext = useCallback(() => {
    if (
      activeStep.subSteps &&
      activeSubStepIndex < activeStep.subSteps.length - 1
    ) {
      goToSubStep(activeSubStepIndex + 1);
      return;
    }

    const nextStep = APPLICATION_JOURNEY_STEPS[activeStepIndex + 1];
    if (nextStep) {
      goToStep(nextStep.key);
    }
  }, [
    activeStep.subSteps,
    activeStepIndex,
    activeSubStepIndex,
    goToStep,
    goToSubStep,
  ]);

  const goPrevious = useCallback(() => {
    if (activeStep.subSteps && activeSubStepIndex > 0) {
      goToSubStep(activeSubStepIndex - 1);
      return;
    }

    const previousStep = APPLICATION_JOURNEY_STEPS[activeStepIndex - 1];
    if (!previousStep) return;

    if (previousStep.subSteps && previousStep.subSteps.length > 0) {
      goToStep(previousStep.key, {
        subStepKey: previousStep.subSteps[previousStep.subSteps.length - 1].key,
      });
      return;
    }

    goToStep(previousStep.key);
  }, [
    activeStep.subSteps,
    activeStepIndex,
    activeSubStepIndex,
    goToStep,
    goToSubStep,
  ]);

  const refreshJourney = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const nextApplication = (await apiGetFdApplication(
        Number(applicationId),
      )) as ISupplierJourneyApplication | null;

      setApplication(nextApplication || undefined);

      if (!nextApplication?.applyingUser?.externalId) {
        setTraderApplication(undefined);
        return;
      }

      const traderApplications = await apiGetUserTraderApplications(
        nextApplication.applyingUser.externalId,
      );
      let supplierApplication =
        getSupplierTraderApplication(traderApplications);

      if (updatedCompany && supplierApplication) {
        supplierApplication = {
          ...supplierApplication,
          company: updatedCompany,
        };
      }

      if (!supplierApplication) {
        setTraderApplication(undefined);
        return;
      }

      const companyForm = lshSelectCompanyForm(supplierApplication);
      if (companyForm?.id) {
        try {
          const fullCompanyForm = await apiGetTraderForm(
            String(companyForm.id),
            nextApplication.applyingUser.externalId,
          );
          if (fullCompanyForm) {
            supplierApplication = {
              ...supplierApplication,
              forms: (supplierApplication.forms || []).map((form) =>
                String(form.id) === String(fullCompanyForm.id)
                  ? fullCompanyForm
                  : form,
              ),
            };
          }
        } catch (formError) {
          console.error('Error fetching supplier trader form', formError);
        }
      }

      setTraderApplication(supplierApplication);
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : JSON.stringify(error);
      setErrorMessage(
        `Could not fetch applications at the moment. Error: ${msg}`,
      );
      console.error('Error fetching supplier application journey', error);
    } finally {
      setIsLoading(false);
    }
  }, [applicationId, updatedCompany]);

  useEffect(() => {
    void refreshJourney();
  }, [refreshJourney, requestedStepPath, requestedSubStepKey]);

  useEffect(() => {
    if (editState !== 'saved') return;

    (async () => {
      try {
        await refreshJourney();
      } finally {
        reduxSetSidePanelEditState('idle');
        reduxSetUpdatedCompany(undefined);
      }
    })();
  }, [editState, refreshJourney]);

  useEffect(() => {
    const nextParams = new URLSearchParams(location.search);
    let changed = false;

    if (!requestedStepPath || activeStep.path !== requestedStepPath) {
      nextParams.set('step', activeStep.path);
      changed = true;
    }

    if (activeStep.subSteps && activeStep.subSteps.length > 0) {
      const hasMatchedSubStep = activeStep.subSteps.some(
        (subStep) => subStep.key === requestedSubStepKey,
      );

      if (!hasMatchedSubStep) {
        nextParams.set('subStep', activeStep.subSteps[0].key);
        changed = true;
      }
    } else if (requestedSubStepKey) {
      nextParams.delete('subStep');
      changed = true;
    }

    if (changed) {
      replaceJourneySearch(nextParams);
    }
  }, [
    activeStep.path,
    activeStep.subSteps,
    location.search,
    replaceJourneySearch,
    requestedStepPath,
    requestedSubStepKey,
  ]);

  const snapshot = useMemo(
    () => adaptSupplierJourneySnapshot(application, traderApplication),
    [application, traderApplication],
  );
  const completion = useMemo(
    () => resolveJourneyCompletionPercent(application),
    [application],
  );
  const applicantSummary = useMemo(
    () => adaptApplicantSummary(application),
    [application],
  );
  const companySummary = useMemo(
    () => adaptCompanySummary(application, traderApplication),
    [application, traderApplication],
  );
  const farmSummary = useMemo(
    () => adaptFarmSummary(application),
    [application],
  );
  const productionSummary = useMemo(
    () => adaptProductionSummary(application),
    [application],
  );
  const financialSummary = useMemo(
    () => adaptFinancialSummary(application),
    [application],
  );
  const documentsSummary = useMemo(
    () => adaptDocumentsSummary(application),
    [application],
  );
  const journeyMeta = useMemo(
    () => adaptJourneyMeta(application),
    [application],
  );

  const contextValue = useMemo<ISupplierApplicationJourneyContextValue>(
    () => ({
      applicationId,
      origin,
      isLoading,
      errorMessage,
      application,
      traderApplication,
      completion,
      activeStep,
      activeStepIndex,
      activeSubStepIndex,
      hasPrevious: activeStepIndex > 0 || activeSubStepIndex > 0,
      hasNext:
        activeStepIndex < APPLICATION_JOURNEY_STEPS.length - 1 ||
        Boolean(
          activeStep.subSteps &&
            activeSubStepIndex < activeStep.subSteps.length - 1,
        ),
      snapshot,
      applicantSummary,
      companySummary,
      farmSummary,
      productionSummary,
      financialSummary,
      documentsSummary,
      journeyMeta,
      setApplication,
      setTraderApplication,
      refreshJourney,
      goToStep,
      goToSubStep,
      goNext,
      goPrevious,
    }),
    [
      applicationId,
      origin,
      isLoading,
      errorMessage,
      application,
      traderApplication,
      completion,
      activeStep,
      activeStepIndex,
      activeSubStepIndex,
      snapshot,
      applicantSummary,
      companySummary,
      farmSummary,
      productionSummary,
      financialSummary,
      documentsSummary,
      journeyMeta,
      refreshJourney,
      goToStep,
      goToSubStep,
      goNext,
      goPrevious,
    ],
  );

  return (
    <SupplierApplicationJourneyContext.Provider value={contextValue}>
      {children}
    </SupplierApplicationJourneyContext.Provider>
  );
}

export function useSupplierApplicationJourney() {
  const context = useContext(SupplierApplicationJourneyContext);

  if (!context) {
    throw new Error(
      'useSupplierApplicationJourney must be used within SupplierApplicationJourneyProvider',
    );
  }

  return context;
}
